import { create } from "zustand";

/**
 * The search the user is currently looking at, published by the V2 search pages for the Quick chat
 * panel (PathFinder Agentic UI lab). The chat wrapper is mounted outside the router and only sees the
 * URL, while the filters live in the search page's state, so the page pushes the context here.
 */
export interface SearchChatContext {
    queryText: string;
    /** Set on instance search only; global search has no instance. */
    instanceId?: string;
    /** Human-readable active filters, e.g. "File type: Page, Document". Sort and paging are not included. */
    filters: string[];
    /**
     * Stable key of the active filters built from raw ids (getSearchFilterKey). The display names in
     * `filters` arrive asynchronously from caches, so they must not decide whether a search is new.
     */
    filterKey: string;
}

/** Stable identity of a search: a different signature means a new conversation. */
export const getSearchChatSignature = (context: SearchChatContext): string =>
    JSON.stringify([context.instanceId ?? "", context.queryText, context.filterKey]);

export const useSearchChatContext = create<{
    context: SearchChatContext | undefined;
    setContext: (context: SearchChatContext) => void;
    /**
     * Clears the store only if it still holds `published` (the object this page last set). The store
     * is shared by the instance and global search pages, so an unconditional clear on unmount could
     * erase what the page the user is landing on has just published.
     */
    clearContext: (published: SearchChatContext | undefined) => void;
}>((set) => ({
    context: undefined,
    setContext: (context) => set({ context }),
    clearContext: (published) => set((state) => (published && state.context === published ? { context: undefined } : {}))
}));
